import { produce } from 'immer';
import {
  TimeSeriesChartSpec,
  TimeSeriesChartStepOption,
  TimeSeriesChartThresholdOptions,
  UNIT_SETTINGS_NAME,
} from '@/core';
import {
  LegendOptionsEditor,
  LegendOptionsEditorProps,
  OptionsEditorColumn,
  OptionsEditorGrid,
  OptionsEditorGroup,
  ResetSettingsButton,
  UnitSelector,
  UnitSelectorProps,
} from '@/plugin-editor-components';
import { ThresholdsEditorProps } from '@/plugin-editor-components/components/ThresholdsEditor/NewThresholdsEditor';
import {
  DEFAULT_UNIT,
  DEFAULT_VISUAL,
  DEFAULT_Y_AXIS,
  TimeSeriesChartOptionsEditorProps,
} from '../time-series-chart-model';
import { TimeSeriesChartThresholdsEditor } from './TimeSeriesChartThresholdsEditor';
import { TooltipOptionsEditor, TooltipOptionsEditorProps } from './TooltipOptionsEditor';
import { VisualOptionsEditor, VisualOptionsEditorProps } from './VisualOptionsEditor';
import { YAxisOptionsEditor, YAxisOptionsEditorProps } from './YAxisOptionsEditor';

export function TimeSeriesChartOptionsEditorGeneralSettings(props: TimeSeriesChartOptionsEditorProps) {
  const { onChange, value } = props;

  const handleLegendChange: LegendOptionsEditorProps['onChange'] = (newLegend) => {
    onChange(
      produce(value, (draft: TimeSeriesChartSpec) => {
        draft.legend = newLegend;
      })
    );
  };

  const handleVisualChange: VisualOptionsEditorProps['onChange'] = (newVisual) => {
    onChange(
      produce(value, (draft: TimeSeriesChartSpec) => {
        draft.visual = newVisual;
      })
    );
  };

  const handleUnitChange: UnitSelectorProps['onChange'] = (newUnit) =>
    onChange(
      produce(value, (draft: TimeSeriesChartSpec) => {
        draft.y_axis = draft.y_axis ?? {};
        draft.y_axis.unit = newUnit;
      })
    );

  const handleYAxisChange: YAxisOptionsEditorProps['onChange'] = (newYAxis) => {
    onChange(
      produce(value, (draft: TimeSeriesChartSpec) => {
        draft.y_axis = newYAxis;
      })
    );
  };

  const handleThresholdsChange: ThresholdsEditorProps<
    TimeSeriesChartStepOption,
    TimeSeriesChartThresholdOptions
  >['onChange'] = (thresholds) => {
    onChange(
      produce(value, (draft: TimeSeriesChartSpec) => {
        draft.thresholds = thresholds;
      })
    );
  };

  const handleTooltipChange: TooltipOptionsEditorProps['onChange'] = (tooltip) => {
    onChange(
      produce(value, (draft: TimeSeriesChartSpec) => {
        draft.tooltip = tooltip;
      })
    );
  };

  return (
    <OptionsEditorGrid>
      <OptionsEditorColumn>
        <OptionsEditorGroup title="Legend">
          <LegendOptionsEditor value={value.legend} onChange={handleLegendChange} />
        </OptionsEditorGroup>
        <VisualOptionsEditor value={value.visual ?? DEFAULT_VISUAL} onChange={handleVisualChange} />
      </OptionsEditorColumn>
      <OptionsEditorColumn>
        <OptionsEditorGroup title={UNIT_SETTINGS_NAME}>
          <UnitSelector value={value.y_axis?.unit ?? DEFAULT_UNIT} onChange={handleUnitChange} />
        </OptionsEditorGroup>
        <YAxisOptionsEditor value={value.y_axis ?? DEFAULT_Y_AXIS} onChange={handleYAxisChange} />
        <OptionsEditorGroup title="Tooltip">
          <TooltipOptionsEditor value={value.tooltip} onChange={handleTooltipChange} />
        </OptionsEditorGroup>
      </OptionsEditorColumn>
      <OptionsEditorColumn>
        <TimeSeriesChartThresholdsEditor thresholds={value.thresholds} onChange={handleThresholdsChange} />
        <ResetSettingsButton value={value} onChange={onChange} />
      </OptionsEditorColumn>
    </OptionsEditorGrid>
  );
}
